import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  ColumnDef,
  flexRender,
} from '@tanstack/react-table';
import { CaretRightIcon, CaretDownIcon } from '@phosphor-icons/react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { TablePagination } from '@/components/common/TablePagination';
import { ProductV2DTO } from 'src/types/application/inventory-management/ProductV2DTO';
import ProductAttributes from './ProductAttributes';

interface ProductsTableProps {
  data: ProductV2DTO[];
  pageInfo?: {
    totalCount: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  isLoading?: boolean;
  isFetching?: boolean;
}

const ProductsTable: React.FC<ProductsTableProps> = ({
  data,
  pageInfo,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isLoading,
  isFetching,
}) => {
  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(
    new Set(),
  );

  const toggleRow = (rowId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    setExpandedRows(newExpandedRows);
  };

  const columns: ColumnDef<ProductV2DTO>[] = [
    {
      id: 'expander',
      header: () => null,
      size: 40,
      cell: ({ row }) => {
        return (
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              toggleRow(row.id);
            }}
            className="h-6 w-6 p-0"
          >
            {expandedRows.has(row.id) ? (
              <CaretDownIcon className="h-4 w-4" />
            ) : (
              <CaretRightIcon className="h-4 w-4" />
            )}
          </Button>
        );
      },
    },
    {
      accessorKey: 'imageUrl',
      header: '',
      size: 70,
      cell: (info) => {
        const value = info.getValue() as string;
        return (
          <div className="w-[60px] h-[60px] rounded-lg flex justify-center items-center bg-gray-100 overflow-hidden">
            {value ? (
              <img
                src={value}
                alt="Product"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-xs text-muted-foreground">No image</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'name',
      header: 'Name',
      size: 100,
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: (info) => {
        const value = info.getValue() as string;
        return (
          <span className="overflow-hidden text-ellipsis whitespace-nowrap block">
            {value || '-'}
          </span>
        );
      },
    },
    {
      accessorKey: 'isActive',
      header: 'Active',
      size: 80,
      cell: (info) => {
        const value = info.getValue() as boolean;
        return <>{value ? 'Yes' : 'No'}</>;
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    pageCount: pageInfo?.totalCount
      ? Math.ceil(pageInfo.totalCount / pageSize)
      : -1,
    manualPagination: true,
    state: {
      pagination: {
        pageIndex: page - 1,
        pageSize,
      },
    },
    onPaginationChange: (updater) => {
      const next =
        typeof updater === 'function'
          ? updater({ pageIndex: page - 1, pageSize })
          : updater;
      onPageChange(next.pageIndex + 1);
      onPageSizeChange(next.pageSize);
      setExpandedRows(new Set());
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <Card className="relative">
      {(isLoading || isFetching) && (
        <div className="absolute inset-0 flex justify-center items-center bg-white/70 z-10">
          <Spinner size="lg" />
        </div>
      )}
      <div>
        {!isLoading && !isFetching && table.getRowModel().rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-8">
            <p className="text-sm text-muted-foreground">
              {`It looks like there aren't any products created yet.`}
            </p>
          </div>
        ) : (
          <>
            <div className="rounded-md">
              <Table className="table-fixed w-full">
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          className="text-left opacity-70 text-[0.65rem] font-bold uppercase whitespace-nowrap"
                          style={{ width: `${header.column.columnDef.size}px` }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.map((row) => (
                    <Collapsible
                      key={row.id}
                      open={expandedRows.has(row.id)}
                      asChild
                    >
                      <>
                        <TableRow
                          onClick={() => toggleRow(row.id)}
                          className="cursor-pointer"
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell
                              key={cell.id}
                              className="text-sm"
                              style={{
                                width: `${cell.column.columnDef.size}px`,
                              }}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                        <TableRow className="hover:bg-transparent">
                          <TableCell
                            colSpan={columns.length}
                            className="p-0 border-0"
                          >
                            <CollapsibleContent className="p-3 bg-muted/30">
                              <ProductAttributes
                                productId={row.original.id}
                                // isExpanded={true} //preload for now
                                isExpanded={expandedRows.has(row.id)}
                              />
                            </CollapsibleContent>
                          </TableCell>
                        </TableRow>
                      </>
                    </Collapsible>
                  ))}
                </TableBody>
              </Table>
            </div>
            <TablePagination
              pagination={{
                pageSize,
                currentPage: page,
                hasNextPage: pageInfo?.hasNextPage ?? false,
                hasPreviousPage: pageInfo?.hasPreviousPage ?? false,
                totalItems: pageInfo?.totalCount,
                startItem:
                  pageInfo?.totalCount && pageInfo.totalCount > 0
                    ? (page - 1) * pageSize + 1
                    : undefined,
                endItem:
                  pageInfo?.totalCount && pageInfo.totalCount > 0
                    ? Math.min(page * pageSize, pageInfo.totalCount)
                    : undefined,
              }}
              onPageSizeChange={(newPageSize) => {
                setExpandedRows(new Set());
                onPageSizeChange(newPageSize);
                onPageChange(1);
              }}
              onPreviousPage={() => {
                setExpandedRows(new Set());
                onPageChange(page - 1);
              }}
              onNextPage={() => {
                setExpandedRows(new Set());
                onPageChange(page + 1);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </>
        )}
      </div>
    </Card>
  );
};

export default ProductsTable;
